import { PathBuilder, ApiRoute, HttpMethod } from '../common/api/routes';
import { LocationId } from '../location/Location';
import {
    IAccessCheckMultiLocationPlatformName,
    IClientAccessCheckPlatformName,
    ILocationAccessCheckPlatformName,
} from '../integration/constants';
import { assertNever } from '../common/lang/errors';
import { ClientId } from '../client/ClientId';

export interface GetServiceInfoResponse {
    result: boolean;
    serviceAccountName: string | null;
}

interface AuthServiceForClientApiParams {
    modelId: ClientId;
}

interface AuthServiceForLocationApiParams {
    modelId: LocationId;
}

function makeClientAuthorizedAndConfiguredPathForService(
    serviceName: IClientAccessCheckPlatformName,
): (path: PathBuilder<AuthServiceForClientApiParams>) => PathBuilder<AuthServiceForClientApiParams> {
    return path =>
        path
            .literal('auth')
            .literal(serviceName)
            .literal('client')
            .param('modelId')
            .literal('isAuthorizedAndConfigured');
}

function makeLocationAuthorizedAndConfiguredPathForService(
    serviceName: ILocationAccessCheckPlatformName,
): (path: PathBuilder<AuthServiceForLocationApiParams>) => PathBuilder<AuthServiceForLocationApiParams> {
    return path =>
        path
            .literal('auth')
            .literal(serviceName)
            .literal('location')
            .param('modelId')
            .literal('isAuthorizedAndConfigured');
}

function makeAuthorizedAndConfiguredLocationsPathForService(
    serviceName: IAccessCheckMultiLocationPlatformName,
): (path: PathBuilder<void>) => PathBuilder<void> {
    return path => path.literal('auth').literal(serviceName).literal('authorizedAndConfiguredLocations');
}

export const AuthServiceApi = {
    getAuthorizedAndConfiguredLocationsForClubReady: new ApiRoute<LocationId[], void, void, void>(
        HttpMethod.Get,
        makeAuthorizedAndConfiguredLocationsPathForService('clubReady'),
    ),
    getAuthorizedAndConfiguredLocationsForFacebook: new ApiRoute<LocationId[], void, void, void>(
        HttpMethod.Get,
        makeAuthorizedAndConfiguredLocationsPathForService('facebook'),
    ),
    getAuthorizedAndConfiguredLocationsForFitware: new ApiRoute<LocationId[], void, void, void>(
        HttpMethod.Get,
        makeAuthorizedAndConfiguredLocationsPathForService('fitware'),
    ),
    getAuthorizedAndConfiguredLocationsForGoogleMyBusiness: new ApiRoute<LocationId[], void, void, void>(
        HttpMethod.Get,
        makeAuthorizedAndConfiguredLocationsPathForService('googleMyBusiness'),
    ),
    getAuthorizedAndConfiguredLocationsForInstagram: new ApiRoute<LocationId[], void, void, void>(
        HttpMethod.Get,
        makeAuthorizedAndConfiguredLocationsPathForService('instagram'),
    ),
    getAuthorizedAndConfiguredLocationsForLinkedIn: new ApiRoute<LocationId[], void, void, void>(
        HttpMethod.Get,
        makeAuthorizedAndConfiguredLocationsPathForService('linkedIn'),
    ),
    getAuthorizedAndConfiguredLocationsForTwitter: new ApiRoute<LocationId[], void, void, void>(
        HttpMethod.Get,
        makeAuthorizedAndConfiguredLocationsPathForService('twitter'),
    ),
    isAuthorizedAndConfiguredForCampaignMonitor: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('campaignMonitor')),
    isAuthorizedAndConfiguredForClubReady: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('clubReady')),
    isAuthorizedAndConfiguredForFacebook: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('facebook')),
    isAuthorizedAndConfiguredForFitware: new ApiRoute<{ result: boolean }, AuthServiceForLocationApiParams, void, void>(
        HttpMethod.Get,
        makeLocationAuthorizedAndConfiguredPathForService('fitware'),
    ),
    isAuthorizedAndConfiguredForInstagram: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('instagram')),
    isAuthorizedAndConfiguredForTwitter: new ApiRoute<{ result: boolean }, AuthServiceForLocationApiParams, void, void>(
        HttpMethod.Get,
        makeLocationAuthorizedAndConfiguredPathForService('twitter'),
    ),
    isClientAuthorizedAndConfiguredForFour51: new ApiRoute<
        { result: boolean },
        AuthServiceForClientApiParams,
        void,
        void
    >(HttpMethod.Get, makeClientAuthorizedAndConfiguredPathForService('four51')),
    isLocationAuthorizedAndConfiguredForFour51: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('four51')),
    isLocationAuthorizedAndConfiguredForGoogleMyBusiness: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('googleMyBusiness')),
    isLocationAuthorizedAndConfiguredForLinkedIn: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('linkedIn')),
    isLocationAuthorizedAndConfiguredForMailchimp: new ApiRoute<
        { result: boolean },
        AuthServiceForLocationApiParams,
        void,
        void
    >(HttpMethod.Get, makeLocationAuthorizedAndConfiguredPathForService('mailchimp')),
};

export function getIsClientAuthorizedAndConfiguredApiRoute(
    serviceName: IClientAccessCheckPlatformName,
): ApiRoute<{ result: boolean }, AuthServiceForClientApiParams> {
    let apiRoute;
    switch (serviceName) {

        case 'four51':
            apiRoute = AuthServiceApi.isClientAuthorizedAndConfiguredForFour51;
            break;
        default:
            throw assertNever(serviceName);
    }
    return apiRoute;
}

export function getIsLocationAuthorizedAndConfiguredApiRoute(
    serviceName: ILocationAccessCheckPlatformName,
): ApiRoute<{ result: boolean }, AuthServiceForLocationApiParams> {
    let apiRoute;
    switch (serviceName) {
        case 'campaignMonitor':
            apiRoute = AuthServiceApi.isAuthorizedAndConfiguredForCampaignMonitor;
            break;
        case 'clubReady':
            apiRoute = AuthServiceApi.isAuthorizedAndConfiguredForClubReady;
            break;
        case 'facebook':
            apiRoute = AuthServiceApi.isAuthorizedAndConfiguredForFacebook;
            break;
        case 'fitware':
            apiRoute = AuthServiceApi.isAuthorizedAndConfiguredForFitware;
            break;
        case 'four51':
            apiRoute = AuthServiceApi.isLocationAuthorizedAndConfiguredForFour51;
            break;
        case 'googleMyBusiness':
            apiRoute = AuthServiceApi.isLocationAuthorizedAndConfiguredForGoogleMyBusiness;
            break;
        case 'instagram':
            apiRoute = AuthServiceApi.isAuthorizedAndConfiguredForInstagram;
            break;
        case 'linkedIn':
            apiRoute = AuthServiceApi.isLocationAuthorizedAndConfiguredForLinkedIn;
            break;
        case 'mailchimp':
            apiRoute = AuthServiceApi.isLocationAuthorizedAndConfiguredForMailchimp;
            break;
        case 'twitter':
            apiRoute = AuthServiceApi.isAuthorizedAndConfiguredForTwitter;
            break;
        default:
            throw assertNever(serviceName);
    }
    return apiRoute;
}

export function getAuthorizedAndConfiguredLocationsApiRoute(
    serviceName: IAccessCheckMultiLocationPlatformName,
): ApiRoute<LocationId[]> {
    let apiRoute;
    switch (serviceName) {
        case 'clubReady':
            apiRoute = AuthServiceApi.getAuthorizedAndConfiguredLocationsForClubReady;
            break;
        case 'facebook':
            apiRoute = AuthServiceApi.getAuthorizedAndConfiguredLocationsForFacebook;
            break;
        case 'fitware':
        case 'fitwareAutomatedEmail':
            apiRoute = AuthServiceApi.getAuthorizedAndConfiguredLocationsForFitware;
            break;
        case 'googleMyBusiness':
            apiRoute = AuthServiceApi.getAuthorizedAndConfiguredLocationsForGoogleMyBusiness;
            break;
        case 'instagram':
            apiRoute = AuthServiceApi.getAuthorizedAndConfiguredLocationsForInstagram;
            break;
        case 'linkedIn':
            apiRoute = AuthServiceApi.getAuthorizedAndConfiguredLocationsForLinkedIn;
            break;
        case 'twitter':
            apiRoute = AuthServiceApi.getAuthorizedAndConfiguredLocationsForTwitter;
            break;
        default:
            throw assertNever(serviceName);
    }
    return apiRoute;
}
